<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FangateAPI from 'dashboard/api/fangate';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      botToken: '',
      // Optional Fangate token entered alongside the Telegram bot token.
      // Saved to localStorage as `fangate_token_<inboxId>` after the
      // Telegram channel is successfully created (we only know the id then).
      fangateToken: '',
      // Validation state for the Fangate token. null = untested, true/false
      // = validated. Resets to null whenever the token string changes.
      fangateValid: null,
      fangateError: '',
      isValidating: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    hasFangateToken() {
      return this.fangateToken.trim().length > 0;
    },
  },
  validations: {
    botToken: { required },
  },
  watch: {
    fangateToken() {
      // Any edit invalidates a previous "tested ok" state — force a re-test.
      this.fangateValid = null;
      this.fangateError = '';
    },
  },
  methods: {
    async testFangateToken() {
      const t = this.fangateToken.trim();
      if (!t) return;
      this.isValidating = true;
      this.fangateError = '';
      try {
        await FangateAPI.validateToken(t);
        this.fangateValid = true;
      } catch (e) {
        this.fangateValid = false;
        this.fangateError = e?.message || 'Token validation failed.';
      } finally {
        this.isValidating = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const telegramChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            channel: {
              type: 'telegram',
              bot_token: this.botToken,
            },
          }
        );

        // Telegram inbox is now live in the DB → we have an inbox id, so we
        // can persist the optional Fangate token under fangate_token_<id>.
        // The token survives F5 / browser restart because it lives in
        // localStorage; the Chat-Account edit modal can rotate it later.
        const fang = this.fangateToken.trim();
        if (fang) {
          FangateAPI.setTokenForInbox(telegramChannel.id, fang);
        }

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: telegramChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <!-- Telegram bot token -->
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.botToken.$error }">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.BOT_TOKEN.LABEL') }}
          <input
            v-model="botToken"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.BOT_TOKEN.PLACEHOLDER')
            "
            @blur="v$.botToken.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.BOT_TOKEN.SUBTITLE') }}
        </p>
      </div>

      <!-- Optional Fangate token. Linked to this Telegram inbox so the
           creator can share products from the chat side-panel right after
           setup. Skippable — the Chat-Account edit modal can add/rotate it
           later if the creator doesn't have a key at sign-up time. -->
      <div class="flex-shrink-0 flex-grow-0 mt-6">
        <div class="flex items-center gap-2 mb-2">
          <label class="text-sm font-semibold m-0">
            Fangate API token
            <span class="text-slate-400 font-normal">(optional)</span>
          </label>
          <!-- Status pill mirroring validation result. Untested → hidden. -->
          <span
            v-if="fangateValid === true"
            class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
          >
            ✓ Verified
          </span>
          <span
            v-else-if="fangateValid === false"
            class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
          >
            ✗ Invalid
          </span>
        </div>
        <div class="flex gap-2 items-stretch">
          <input
            v-model="fangateToken"
            type="text"
            placeholder="Paste the Fangate API token from your dashboard…"
            class="flex-1"
          />
          <button
            type="button"
            class="px-4 py-2 rounded-md border border-slate-300 bg-slate-50 text-slate-700 font-semibold text-sm hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!hasFangateToken || isValidating"
            @click="testFangateToken"
          >
            {{ isValidating ? 'Testing…' : 'Test connection' }}
          </button>
        </div>
        <p class="help-text">
          Optional. If provided, this token is linked to the new Telegram
          inbox so the creator can browse + share their Fangate products
          directly from the chat side-panel. Generate one in Fangate →
          Settings → API keys.
        </p>
        <p
          v-if="fangateError"
          class="text-xs text-red-600 mt-1"
        >
          {{ fangateError }}
        </p>
      </div>

      <div class="w-full mt-6">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_CHANNEL.SUBMIT_BUTTON')"
        />
        <p
          v-if="hasFangateToken && fangateValid !== true"
          class="text-xs text-slate-500 mt-2"
        >
          Tip: click "Test connection" to verify the Fangate token before
          submitting. Saving will still work, but a bad token only fails
          later when you open the side-panel.
        </p>
      </div>
    </form>
  </div>
</template>
